import { useState } from "react";
import { bewerteStorno, stufenLabel } from "@/lib/cancellation";
import { formatEUR } from "@/lib/bookingModel";

/**
 * Die Stornoregelung als Zusage — nicht als Belehrung.
 *
 * Rechtlich steht sie in den AGB des Vermieters, die der Kunde beim Anfragen
 * ohnehin akzeptiert. Wir zeigen sie, weil eine gute Regelung einem
 * Zweifelnden die Entscheidung abnimmt. Eine schlechte nimmt sie ihm nicht ab
 * — sie erzeugt nur Zweifel, wo vorher keine waren. Also: eine Zeile, wenn es
 * etwas zuzusagen gibt, sonst nichts.
 *
 *   variante="karte"      erster Schritt, unter der Ausstattung. Der Kunde
 *                         vergleicht noch und hat keinen Termin gewählt, also
 *                         steht dort die Regel ("bis 22 Tage vor dem
 *                         Miettag") statt eines Datums. Ohne die Frist sähe
 *                         die Zeile wie ein uneingeschränktes Versprechen
 *                         aus — die Bedingung dazuzuschreiben macht sie
 *                         glaubwürdiger, nicht schwächer.
 *   variante="warenkorb"  am Absendeknopf. Hier entscheidet er sich, hier
 *                         sitzt die Sorge — und hier steht ein echtes Datum,
 *                         weil der Termin inzwischen gewählt ist. Mittig, wie
 *                         die übrige Beruhigung unter dem Knopf.
 *   variante="zahlung"    an "Zur Anzahlung", nachdem der Vermieter zugesagt
 *                         hat. Der teuerste Absprung im ganzen Ablauf: Der
 *                         Termin ist reserviert, der Vermieter hat ja gesagt,
 *                         und jetzt soll zum ersten Mal Geld fließen. Gleicher
 *                         Aufbau, aber linksbündig wie der Kasten drumherum.
 *
 * Die vollständigen Fristen hängen an einem kleinen Link und klappen nie von
 * selbst auf. Wer sie sucht, findet sie; wer nicht, wird nicht mit einer
 * absteigenden Prozentreihe konfrontiert, während er gerade zusagt.
 */
export default function Stornobedingungen({
  cancellation,
  partnerName,
  eventDate,
  gesamtpreis,
  agbUrl,
  variante = "karte",
}) {
  const [offen, setOffen] = useState(false);
  const { punkte, staffel } = bewerteStorno(cancellation, {
    eventDate,
    heute: eventDate ? new Date() : undefined,
    gesamtpreis,
  });

  // Nichts zuzusagen und nichts zu zeigen: dann auch nichts sagen. An dieser
  // Stelle ist noch nichts verbindlich — die Bedingungen stehen in den AGB
  // des Vermieters, die im Häkchen darunter verlinkt sind.
  if (!punkte.length && !staffel.stornierbar) return null;

  const fristenLink = staffel.stornierbar ? (
    <button
      type="button"
      onClick={() => setOffen((o) => !o)}
      className="text-xs text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-800"
      aria-expanded={offen}
    >
      {offen ? "Weniger" : "Alle Fristen"}
    </button>
  ) : null;

  // Am Absendeknopf und an der Anzahlung mittig, wie die übrige Beruhigung
  // dort; auf der Anbieterkarte linksbündig wie alles andere in der Karte.
  const mittig = variante === "warenkorb";

  return (
    <div
      className={[
        variante === "karte" ? "mt-4 border-t border-slate-100 pt-3" : "mt-3",
        mittig ? "text-center" : "",
      ].join(" ")}
    >
      <ul className={`space-y-1 ${mittig ? "inline-block text-left" : ""}`}>
        {punkte.map((p) => (
          <li key={p.art} className="flex items-baseline gap-1.5">
            <Haken />
            <span>
              <span className="text-sm font-semibold text-slate-900">
                {p.text}
              </span>{" "}
              <span className="text-sm text-slate-500">{p.frist}</span>
            </span>
          </li>
        ))}
      </ul>

      {fristenLink && (
        <div className={punkte.length ? "mt-1.5" : ""}>{fristenLink}</div>
      )}

      {offen && (
        <Fristen staffel={staffel} partnerName={partnerName} agbUrl={agbUrl} />
      )}
    </div>
  );
}

function Haken() {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 translate-y-0.5 text-emerald-600"
    >
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity="0.13" />
      <path
        d="M4.5 8.4l2.4 2.4L11.5 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Die vollständige Staffel — nur auf Klick.
 *
 * Hier darf sie ausführlich sein: Wer den Link angeklickt hat, will genau das
 * wissen. Auch die Teilerstattungen, die im Hinweis bewusst fehlen. Lücken
 * werden nicht gefüllt.
 */
function Fristen({ staffel, partnerName, agbUrl }) {
  const wer = partnerName || "der Anbieter";
  return (
    <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2.5 text-left ring-1 ring-slate-200">
      {staffel.stufen.some((s) => s.betrag !== null) && (
        <p className="mb-1.5 text-right text-[10px] uppercase tracking-wide text-slate-400">
          Erstattung vom Mietpreis
        </p>
      )}
      <ol className="space-y-1">
        {staffel.stufen.map((s) => (
          <li
            key={`${s.vonTag}-${s.bisTag}`}
            className={`flex items-baseline justify-between gap-4 text-xs ${
              s.giltJetzt ? "font-semibold text-slate-900" : "text-slate-600"
            }`}
          >
            <span>
              {stufenLabel(s)}
              {s.giltJetzt && (
                <span className="ml-1.5 font-normal text-[10px] uppercase tracking-wide text-slate-400">
                  gilt jetzt
                </span>
              )}
            </span>
            {/* Der Preis steht auf derselben Seite — dann soll niemand die
                Prozente im Kopf darauf anwenden müssen. Der Betrag ist die
                Zahl, um die es geht; die Prozentangabe bleibt daneben, weil
                sie das ist, was der Vermieter zugesagt hat. */}
            <span className="shrink-0 whitespace-nowrap tabular-nums">
              {s.betrag === null ? (
                <>{s.prozent} % zurück</>
              ) : (
                <>
                  <span className="text-slate-400">{s.prozent} %</span>{" "}
                  {formatEUR(s.betrag)}
                </>
              )}
            </span>
          </li>
        ))}
      </ol>

      {staffel.luecken.map((l) => (
        <p
          key={l.vonTag}
          className="mt-2 text-xs leading-relaxed text-slate-500"
        >
          {l.bisTag === null
            ? `Für eine Absage früher als ${l.vonTag} Tage vor dem Miettag hat ${wer} nichts hinterlegt.`
            : `Für eine Absage ${l.vonTag}–${l.bisTag} Tage vor dem Miettag hat ${wer} nichts hinterlegt.`}
        </p>
      ))}

      <p className="mt-2 text-xs leading-relaxed text-slate-400">
        Es gelten die{" "}
        {agbUrl ? (
          <a
            href={agbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-slate-300 underline-offset-2 hover:text-slate-700"
          >
            AGB von {wer}
          </a>
        ) : (
          <>AGB von {wer}</>
        )}
        .
      </p>
    </div>
  );
}
